import React, { useMemo } from "react";

function computePath(values, width, height, padding) {
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const step = values.length > 1 ? innerW / (values.length - 1) : innerW;

  return values
    .map((value, index) => {
      const x = padding.left + (index * step);
      const pct = max === min ? 0.5 : (value - min) / (max - min);
      const y = padding.top + innerH - (pct * innerH);
      return `${x},${y}`;
    })
    .join(" ");
}

function computeArea(values, width, height, padding) {
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const step = values.length > 1 ? innerW / (values.length - 1) : innerW;
  const points = values.map((value, index) => {
    const x = padding.left + (index * step);
    const pct = max === min ? 0.5 : (value - min) / (max - min);
    const y = padding.top + innerH - (pct * innerH);
    return `${x},${y}`;
  });

  const lastX = padding.left + (values.length > 1 ? innerW : 0);
  const baseY = padding.top + innerH;
  return `M ${padding.left} ${baseY} L ${points.join(" L ")} L ${lastX} ${baseY} Z`;
}

function LineChart({ labels = [], values = [], color = "#8bffb0", secondaryColor = "rgba(139,255,176,0.14)" }) {
  const width = 1000;
  const height = 280;
  const padding = useMemo(() => ({ top: 18, right: 16, bottom: 34, left: 20 }), []);

  const { path, area, gridLines, ticks, maxValue } = useMemo(() => {
    const safeValues = values.length ? values : [0];
    const max = Math.max(...safeValues, 1);
    const stepCount = 4;
    const lines = Array.from({ length: stepCount + 1 }, (_, index) => {
      const pct = index / stepCount;
      return {
        y: padding.top + (height - padding.top - padding.bottom) * pct,
        value: Math.round(max - (max * pct)),
      };
    });

    const tickIndices = labels.length <= 3
      ? labels.map((_, index) => index)
      : [0, Math.floor(labels.length / 2), labels.length - 1];

    return {
      path: computePath(safeValues, width, height, padding),
      area: computeArea(safeValues, width, height, padding),
      gridLines: lines,
      ticks: tickIndices,
      maxValue: max,
    };
  }, [labels, padding, values]);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
          Peak: <span style={{ color, fontWeight: 700 }}>{maxValue.toLocaleString("en-IN")}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 99, background: color }} />
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>Live trend</span>
        </div>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Live line chart">
        {gridLines.map((line) => (
          <line
            key={line.y}
            x1={padding.left}
            x2={width - padding.right}
            y1={line.y}
            y2={line.y}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
          />
        ))}
        {path !== "0,0" && (
          <>
            <path d={area} fill={secondaryColor} />
            <polyline
              points={path}
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}

        {ticks.map((index) => {
          const label = labels[index] || "";
          const x = labels.length > 1 ? padding.left + ((width - padding.left - padding.right) / (labels.length - 1)) * index : padding.left;
          return (
            <g key={`${label}-${index}`}>
              <line
                x1={x}
                x2={x}
                y1={height - padding.bottom}
                y2={height - padding.bottom + 6}
                stroke="rgba(255,255,255,0.18)"
                strokeWidth="1"
              />
              <text
                x={x}
                y={height - 10}
                fill="rgba(255,255,255,0.58)"
                fontSize="11"
                textAnchor="middle"
              >
                {label}
              </text>
            </g>
          );
        })}

        <text x={padding.left} y={14} fill="rgba(255,255,255,0.45)" fontSize="11">
          High
        </text>
        <text x={padding.left} y={height - 16} fill="rgba(255,255,255,0.45)" fontSize="11">
          Low
        </text>
      </svg>
    </div>
  );
}

export default React.memo(LineChart);
